import React, { useEffect, useState } from 'react';
import { Form, Input, notification } from 'antd';
import { useTranslation } from 'react-i18next';
import './EmailEdit.scss';
import DrawerFormContainer from 'components/drawerFormContainer/DrawerFormContainer';
import { fetchAndUpdateProfileWithPartialProperties } from 'services/profileService';
import { LoyaltyAccount } from 'types/dto/profile';
import { useNavigate } from 'react-router-dom';
import { buildEmailSentPath } from 'utils/pathBuilder';
import { useSession } from 'context/SessionContext';
// import { SessionDTO } from 'types/dto/login';
import { sendVerificationEmail } from 'services/emailService';

interface EmailEditProps {
  visible: boolean;
  onClose: () => void;
  // onSave?: () => void;
  loyaltyAccount?: LoyaltyAccount;
}

export interface EmailFormData {
  email: string;
}

const EmailEdit: React.FC<EmailEditProps> = ({
  visible,
  onClose,
  // onSave,
  loyaltyAccount,
}) => {
  const { t } = useTranslation('profile');
  const { t: tCommon } = useTranslation('common');
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { session } = useSession();
  const emailValue = Form.useWatch('email', form);

  const hasEmail = loyaltyAccount?.email && loyaltyAccount?.email !== '';
  const currentEmail = loyaltyAccount?.email || '';

  const handleValuesChange = (changedValues: any) => {
    const fieldNames = Object.keys(changedValues);
    if (fieldNames.length > 0) {
      const fieldsToUpdate = fieldNames.map(name => ({
        name: name as any,
        errors: [],
      }));
      form.setFields(fieldsToUpdate);
    }
  };

  const handleSave = async () => {
    try {
      setIsSubmitting(true);
      const values = await form.validateFields();
      // console.log('Email saved:', values);

      //this will trigger a verification email
      await fetchAndUpdateProfileWithPartialProperties(loyaltyAccount?.unique_id || '', { email: values.email, });

      await sendVerificationEmail(values.email);

      if (!session) {
        console.error('Session is missing');
        return;
      }

      // setSession({
      //   ...session,
      //   _ml: values.email
      // } as SessionDTO);

      const profileData = {
        firstName: loyaltyAccount?.first_name,
        lastName: loyaltyAccount?.last_name,
        titleCode: loyaltyAccount?.salutation,
        email: values.email,
      };

      //Redirect to email verification page
      navigate(buildEmailSentPath(), {
        state: {
          session: session,
          registrationData: profileData,
          fromConfirm: true
        }
      });

      //onSave?.();
    } catch (error) {
      console.error('Validation failed:', error);
      notification.error({
        message: tCommon('error.updateEmailFailed'),
        description: (error as Error).message,
        duration: 1.5,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Email validation rule
  const emailValidationRules = [
    {
      required: true,
      message: t('emailEdit.emailRequired'),
    },
    {
      type: 'email' as const,
      message: t('emailEdit.emailInvalid'),
    },
  ];

  useEffect(() => {
    if (visible) {
      form.resetFields();
    }
  }, [form, visible]);

  const isSaveDisabled =
    !emailValue ||
    emailValue.trim() === currentEmail.trim();

  return (
    <DrawerFormContainer
      title={hasEmail ? t('emailEdit.editEmail') : t('emailEdit.title')}
      visible={visible}
      onClose={() => {
        onClose();
      }}
      onSave={handleSave}
      saveButtonTitle={t('emailEdit.saveAndVerify')}
      isSubmitting={isSubmitting}
      isSaveButtonDisabled={isSaveDisabled}
    >
      <Form
        form={form}
        layout="vertical"
        validateTrigger="onSubmit"
        onValuesChange={handleValuesChange}
        initialValues={{ email: loyaltyAccount?.email }}
        className="email-form"
      >
        <Form.Item
          name="email"
          label={t('emailEdit.emailLabel')}
          rules={emailValidationRules}
        >
          <Input
            placeholder={t('emailEdit.emailPlaceholder')}
            className="form-input email-input"
            type="email"
          />
        </Form.Item>
        <div className="email-verification-hint">
          {t('emailEdit.verificationHint')}
        </div>
      </Form>
    </DrawerFormContainer>
  );
};

export default EmailEdit;